import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectAutocomletePlayers, selectNicknameToFind } from '../containers/StatPage/selectors';
import {
  changePlayernameToFind,
  findStats,
  setLoadedPlayers,
  dropFiltersToSettingsState,
} from '../containers/StatPage/actions';
import { getPlayerStatsHref } from '../containers/StatPage/utils';
import AutocompletePlayerModel from '../viewModel/AutocompletePlayerModel';

export type PlayerSearchOption = {
  value: number;
  label: string;
  player: AutocompletePlayerModel;
  href: string;
};

export default function useSearchPlayers() {
  const dispatch = useDispatch();
  const isPlayerSearchFocusedRef = React.useRef(false);

  const nicknameToFind = useSelector(selectNicknameToFind);
  const autocompletePlayers = useSelector(selectAutocomletePlayers);

  const changeNicknameToFind = React.useCallback(
    (value: string) => {
      dispatch(changePlayernameToFind(value));
    },
    [dispatch],
  );

  const searchPlayer = React.useCallback(
    (value: string) => {
      if (value === '' && !isPlayerSearchFocusedRef.current) {
        return;
      }

      changeNicknameToFind(value);
    },
    [changeNicknameToFind],
  );

  const focusPlayerSearch = React.useCallback(() => {
    isPlayerSearchFocusedRef.current = true;
  }, []);

  const blurPlayerSearch = React.useCallback(() => {
    isPlayerSearchFocusedRef.current = false;
  }, []);

  const selectPlayerOption = React.useCallback(
    (player: AutocompletePlayerModel) => {
      dispatch(dropFiltersToSettingsState());
      dispatch(
        findStats({
          playername: player.playername,
          pokersiteId: player.pokersiteId,
        }),
      );
    },
    [dispatch],
  );

  const selectPlayer = React.useCallback(
    (value: number) => {
      const player = autocompletePlayers.find((x) => x.id === value);

      if (player) {
        selectPlayerOption(player);
      }
    },
    [autocompletePlayers, selectPlayerOption],
  );

  const clearAutocompletePlayers = React.useCallback(() => {
    dispatch(setLoadedPlayers([]));
  }, [dispatch]);

  const options = React.useMemo<Array<PlayerSearchOption>>(() => {
    return autocompletePlayers.map((x) => ({
      value: x.id,
      label: x.label,
      player: x,
      href: getPlayerStatsHref(x.playername, x.pokersiteId),
    }));
  }, [autocompletePlayers]);

  return {
    nicknameToFind,
    changeNicknameToFind,
    searchPlayer,
    focusPlayerSearch,
    blurPlayerSearch,
    selectPlayer,
    clearAutocompletePlayers,
    autocompletePlayers,
    options,
    selectPlayerOption,
  };
}
